import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabase";
import api from "../lib/api";
import {
  clearInviteToken,
  inviteLinkExpired,
  inviteLinkFailed,
  landedAsAuthInvite,
  readInviteToken,
} from "../lib/inviteToken";
import { clearContinuation, rememberContinuation } from "../lib/authContinuation";
import { apiCode, apiMessage, roleLabel } from "../lib/memberManagement";
import { reportError } from "../lib/errorReporter";
import { fmtDateTime } from "../lib/format";
import InviteShell from "../components/invite/InviteShell";
import InviteSetPassword from "../components/invite/InviteSetPassword";

// How long to wait for the Supabase SDK to turn an invitation link into a
// session before the screen gives up on it.
const SESSION_TIMEOUT_MS = 10000;

const GENERIC = "Възникна неочаквана грешка. Опитайте отново по-късно.";

/** The token travels in the BODY, never in a URL. Absent in the new-user flow. */
function tokenBody() {
  const token = readInviteToken();
  return token ? { token } : {};
}

/**
 * Tenant (Мениджър / Търговец) invitation acceptance.
 *
 * PUBLIC route, deliberately outside the organization guards: the invited person
 * belongs to no organization yet, which is exactly what acceptance fixes.
 *
 * TOKEN HANDLING — nothing here is authorized by a URL:
 *   * the one-time token arrives in the FRAGMENT (#token=...), is captured
 *     synchronously at bundle boot and removed from the address bar with
 *     history.replaceState BEFORE this component renders (lib/inviteToken.js);
 *   * it lives in a module variable only. Never localStorage, never
 *     sessionStorage, never a cookie, never a log line, never an error report,
 *     never a query string, and it is never appended to a URL by browser code;
 *   * navigating to /login and back is a client-side route change in the SAME
 *     document, so the token survives it without ever being persisted. Only the
 *     RETURN PATH is remembered, in sessionStorage, from a fixed allow-list;
 *   * it is dropped the moment acceptance succeeds or fails terminally.
 *
 * TWO FLOWS, both ending in the same explicit user action:
 *   existing_user  the person already has an OfferTrace account and follows an
 *                  OfferTrace security e-mail carrying the token;
 *   new_user       the person follows a SUPABASE ADMIN invitation. Supabase owns
 *                  the message and the password establishment; this screen reuses
 *                  the verified InviteSetPassword component from the Owner flow -
 *                  it calls supabase.auth.updateUser, so OfferTrace never handles
 *                  or stores a password and no second password mechanism exists.
 *
 * The organization, the role and the seat are decided by the database, from the
 * verified session identity - never from an editable form field.
 */
export default function AcceptMemberInvite() {
  const nav = useNavigate();
  const { loading, session, refreshOrgs, switchOrg } = useAuth();
  const [phase, setPhase] = useState(() => (inviteLinkFailed() ? "link_error" : "loading"));
  const [preview, setPreview] = useState(null);
  const [accepted, setAccepted] = useState(null);
  const [error, setError] = useState("");
  const [waited, setWaited] = useState(false);
  const started = useRef(false);
  const inFlight = useRef(false);
  const authInvite = landedAsAuthInvite();

  // Belt and braces on top of the fragment: no Referer may ever leave this
  // route, whatever a future link on the page might be.
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "referrer";
    meta.content = "no-referrer";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);

  useEffect(() => {
    if (!authInvite || session || waited) return undefined;
    const timer = setTimeout(() => setWaited(true), SESSION_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [authInvite, session, waited]);

  const load = useCallback(async () => {
    try {
      const r = await api.post("/member-invitations/preview", tokenBody(), {
        skipOrgHeader: true,
      });
      setPreview(r.data);
      const state = r.data?.state;
      if (state === "valid") {
        setPhase(authInvite && r.data?.flow === "new_user" ? "set_password" : "ready");
      } else if (state === "requires_authentication") {
        setPhase("requires_authentication");
      } else if (state === "already_accepted" || state === "already_member") {
        setPhase("already");
      } else {
        setPhase("refused");
      }
    } catch (err) {
      reportError("member_invite.preview");
      setError(apiMessage(err, GENERIC));
      setPhase("refused");
    }
  }, [authInvite]);

  useEffect(() => {
    if (started.current || loading || phase !== "loading") return;
    // Give the SDK a chance to establish the session an invitation link carries.
    if (authInvite && !session && !waited) return;
    started.current = true;
    load();
  }, [authInvite, load, loading, phase, session, waited]);

  // The continuation is a PATH, never the token. It lets /login send the person
  // straight back here after signing in.
  useEffect(() => {
    if (phase === "requires_authentication") rememberContinuation("/auth/accept-invite");
  }, [phase]);

  const accept = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setError("");
    setPhase("working");
    try {
      const r = await api.post("/member-invitations/accept", tokenBody(), {
        skipOrgHeader: true,
      });
      // The token has done its one job. Drop it before anything else runs.
      clearInviteToken();
      clearContinuation();
      setAccepted(r.data);
      try {
        await refreshOrgs();
      } catch (err) {
        reportError("member_invite.refresh_orgs");
      }
      if (r.data?.organization_id) switchOrg(r.data.organization_id);
      setPhase(r.data?.already_accepted ? "already" : "success");
    } catch (err) {
      reportError("member_invite.accept");
      const code = apiCode(err);
      setError(apiMessage(err, GENERIC));
      if (code === "not_authenticated") {
        setPhase("requires_authentication");
      } else {
        clearInviteToken();
        setPhase("refused");
      }
    } finally {
      inFlight.current = false;
    }
  }, [refreshOrgs, switchOrg]);

  const setPasswordThenAccept = useCallback(
    async (password) => {
      if (inFlight.current) return;
      inFlight.current = true;
      setError("");
      const { error: updateError } = await supabase.auth.updateUser({ password });
      inFlight.current = false;
      if (updateError) {
        reportError("member_invite.set_password");
        if (updateError.code === "weak_password") {
          setError("Паролата е твърде слаба. Изберете по-дълга и по-сложна парола.");
        } else if (updateError.status === 401 || updateError.status === 403) {
          setPhase("link_error");
        } else {
          setError(GENERIC);
        }
        return;
      }
      await accept();
    },
    [accept],
  );

  const organizationName =
    accepted?.organization_name || preview?.organization_name || "";
  const label = accepted?.role_label || preview?.role_label || roleLabel(preview?.target_role);

  const body = () => {
    if (phase === "loading") {
      return (
        <div className="qw-empty" data-testid="accept-member-invite-checking">
          Проверяваме поканата…
        </div>
      );
    }

    if (phase === "link_error") {
      return (
        <div data-testid="accept-member-invite-link-error">
          <h1>Връзката не е валидна</h1>
          <p className="lead">
            {inviteLinkExpired()
              ? "Срокът на поканата е изтекъл."
              : "Поканата не е валидна или вече не е достъпна."}
          </p>
          <Link className="qw-btn primary" to="/login" data-testid="accept-member-invite-login">
            Към входа
          </Link>
        </div>
      );
    }

    if (phase === "requires_authentication") {
      // WHY THIS IS BRANCHED: the server tells us WHICH kind of invitation is
      // waiting. A person whose address already has an OfferTrace profile must
      // sign in - and must be told that, plus how to recover the password. A
      // person who has never created one must NEVER be sent to a login screen
      // for a password that does not exist; their way in is the account-setup
      // link Supabase mailed them.
      const flow = preview?.flow;
      const newAccount = flow === "new_user";
      return (
        <div data-testid="accept-member-invite-requires-auth">
          <h1>{newAccount ? "Завършете създаването на профила си" : "Нужен е вход"}</h1>
          {newAccount ? (
            <p className="lead" data-testid="accept-member-invite-setup-required">
              Отворете отново връзката от имейла „Покана“, за да създадете паролата си и да
              приемете поканата.
            </p>
          ) : (
            <p className="lead">За да приемете поканата, първо влезте в профила си.</p>
          )}
          {flow === "existing_user" && (
            <div className="qw-help" data-testid="accept-member-invite-existing-account">
              Този имейл адрес вече има профил в OfferTrace. Влезте с него, за да приемете
              поканата. Ако не си спомняте паролата, поискайте нова и след това отворете
              връзката от имейла отново.
            </div>
          )}
          {organizationName && (
            <div className="qw-help" data-testid="accept-member-invite-organization">
              Организация: <strong>{organizationName}</strong>
            </div>
          )}
          {!newAccount && (
            <Link
              className="qw-btn primary"
              to="/login"
              style={{ marginTop: 12 }}
              data-testid="accept-member-invite-login"
            >
              Към входа
            </Link>
          )}
          {flow === "existing_user" && (
            <Link
              className="qw-btn ghost sm"
              to="/auth/forgot-password"
              style={{ marginTop: 8 }}
              data-testid="accept-member-invite-forgot-password"
            >
              Забравена парола?
            </Link>
          )}
        </div>
      );
    }

    if (phase === "set_password") {
      return <InviteSetPassword busy={false} error={error} onSubmit={setPasswordThenAccept} />;
    }

    if (phase === "success") {
      return (
        <div data-testid="accept-member-invite-success">
          <h1>Поканата е приета успешно.</h1>
          <p className="lead">
            Вече сте част от {organizationName}
            {label ? ` като ${label}` : ""}.
          </p>
          <button
            type="button"
            className="qw-btn primary"
            style={{ width: "100%", justifyContent: "center", marginTop: 12 }}
            onClick={() => nav("/dashboard", { replace: true })}
            data-testid="accept-member-invite-continue"
          >
            Към приложението
          </button>
        </div>
      );
    }

    if (phase === "already") {
      return (
        <div data-testid="accept-member-invite-already">
          <h1>Поканата вече е приета.</h1>
          <p className="lead">{preview?.message || "Вече сте член на тази организация."}</p>
          <button
            type="button"
            className="qw-btn primary"
            style={{ width: "100%", justifyContent: "center", marginTop: 12 }}
            onClick={() => nav("/dashboard", { replace: true })}
            data-testid="accept-member-invite-continue"
          >
            Към приложението
          </button>
        </div>
      );
    }

    if (phase === "refused") {
      return (
        <div data-testid="accept-member-invite-refused">
          <h1>Поканата не е достъпна</h1>
          <p className="lead" data-testid="accept-member-invite-refused-message">
            {error || preview?.message || "Поканата не е валидна или вече не е достъпна."}
          </p>
          {preview?.hint && (
            <div className="qw-help" data-testid="accept-member-invite-refused-hint">
              {preview.hint}
            </div>
          )}
          <Link className="qw-btn primary" to="/login" data-testid="accept-member-invite-login">
            Към входа
          </Link>
        </div>
      );
    }

    return (
      <div data-testid="accept-member-invite-ready">
        <h1>Покана за присъединяване</h1>
        <p className="lead" data-testid="accept-member-invite-lead">
          Поканени сте в {organizationName}
          {label ? ` като ${label}` : ""}.
        </p>
        {preview?.expires_at && (
          <div className="qw-help" data-testid="accept-member-invite-expires">
            Валидна до: {fmtDateTime(preview.expires_at)}
          </div>
        )}
        {error && (
          <div className="qw-error" data-testid="accept-member-invite-error">
            {error}
          </div>
        )}
        <button
          type="button"
          className="qw-btn primary"
          style={{ width: "100%", justifyContent: "center", marginTop: 12 }}
          onClick={accept}
          disabled={phase === "working"}
          data-testid="accept-member-invite-submit"
        >
          {phase === "working" ? "Приемане…" : "Приеми поканата"}
        </button>
      </div>
    );
  };

  // A tenant invitation is never an Owner invitation: the shell would otherwise
  // introduce a Sales or Manager invitation as „Покана за собственик“, which is
  // what production showed. The role itself is named in the lead above, from the
  // server's own role label.
  return (
    <InviteShell
      title="Покана за присъединяване"
      note="Поканата е лична, важи еднократно и се приема с имейла, на който сте я получили."
    >
      {body()}
    </InviteShell>
  );
}
